import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import PageTransition from '../components/ui/PageTransition';
import SectionHeading from '../components/ui/SectionHeading';
import ArticleCard from '../components/ui/ArticleCard';
import HeroSection from '../components/sections/HeroSection';
import CtaSection from '../components/sections/CtaSection';
import { articles } from '../data/articles';

const AdvicePage = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Web Design & Development Articles | Expert Advice from Consiliuma</title>
        <meta name="description" content="Expert advice on web design, development, SEO, and digital strategy. Learn from our British web experts and stay updated with the latest industry trends and best practices." />
        <meta name="keywords" content="Web Design Tips, Development Tutorials, SEO Guide, Digital Strategy, Web Best Practices" />
        <link rel="canonical" href="/advice" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Web Design & Development Articles | Expert Advice from Consiliuma" />
        <meta property="og:description" content="Expert advice on web design, development, SEO, and digital strategy. Learn from our British web experts and stay updated with the latest industry trends." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://consiliuma.co.uk/advice" />
        <meta property="og:image" content="https://images.pexels.com/photos/1181271/pexels-photo-1181271.jpeg" />
        <meta property="og:site_name" content="Consiliuma" />
        <meta property="og:locale" content="en_GB" />

        {/* Schema.org data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Web Design & Development Articles | Consiliuma",
            "description": "Expert advice on web design, development, SEO, and digital strategy.",
            "url": "https://consiliuma.co.uk/advice",
            "publisher": {
              "@type": "Organization",
              "name": "Consiliuma",
              "logo": {
                "@type": "ImageObject",
                "url": "https://consiliuma.co.uk/favicon.svg"
              }
            },
            "blogPost": articles.map(article => ({
              "@type": "BlogPosting",
              "headline": article.title,
              "description": article.excerpt,
              "image": article.imageUrl,
              "datePublished": article.date,
              "author": {
                "@type": "Organization",
                "name": "Consiliuma"
              },
              "url": `https://consiliuma.co.uk/advice/${article.slug}`
            }))
          })}
        </script>
      </Helmet>

      <main>
        <h1 className="sr-only">Web Design & Development Articles and Expert Advice</h1>

        <HeroSection
          title="Website Advice & Resources"
          subtitle="Practical tips and expert guidance to help you succeed online"
          ctaText="Browse Articles"
          ctaLink="#articles"
          backgroundImage="https://images.pexels.com/photos/1181271/pexels-photo-1181271.jpeg"
        />

        {/* Articles */}
        <section className="py-16 md:py-24" id="articles" aria-labelledby="articles-heading">
          <div className="container-custom">
            <h2 id="articles-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">Latest Articles</h2>
            <p className="text-xl text-secondary mb-12 text-center">Expert advice on web design, development, SEO, and maintenance</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
              {articles.map((article, index) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <ArticleCard
                    title={article.title}
                    excerpt={article.excerpt}
                    slug={article.slug}
                    imageUrl={article.imageUrl}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Topics */}
        <section className="py-16 md:py-24 bg-background-alt" aria-labelledby="topics-heading">
          <div className="container-custom">
            <h2 id="topics-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">Popular Topics</h2>
            <p className="text-xl text-secondary mb-12 text-center">Browse our articles by category</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md text-center border-t-4 border-primary"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">Web Design</h3>
                <p className="text-secondary mb-4">
                  Tips on creating beautiful, user-friendly, and effective website designs.
                </p>
                <a href="/advice" className="text-primary font-semibold hover:text-primary-dark transition-colors">
                  View Articles →
                </a>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md text-center border-t-4 border-primary"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">Development</h3>
                <p className="text-secondary mb-4">
                  Insights on coding best practices, technology choices, and implementation strategies.
                </p>
                <a href="/advice" className="text-primary font-semibold hover:text-primary-dark transition-colors">
                  View Articles →
                </a>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md text-center border-t-4 border-primary"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">SEO</h3>
                <p className="text-secondary mb-4">
                  Strategies to improve your website's visibility and ranking in search engines.
                </p>
                <a href="/advice" className="text-primary font-semibold hover:text-primary-dark transition-colors">
                  View Articles →
                </a>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md text-center border-t-4 border-primary"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">Maintenance</h3>
                <p className="text-secondary mb-4">
                  Advice on keeping your website secure, up-to-date, and performing optimally.
                </p>
                <a href="/advice" className="text-primary font-semibold hover:text-primary-dark transition-colors">
                  View Articles →
                </a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Web Tips */}
        <section className="py-16 md:py-24" aria-labelledby="tips-heading">
          <div className="container-custom">
            <h2 id="tips-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">Quick Web Tips</h2>
            <p className="text-xl text-secondary mb-12 text-center">Practical advice you can implement today</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12 max-w-4xl mx-auto">
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-primary"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">Optimize Your Images</h3>
                <p className="text-secondary">
                  Large images can significantly slow down your website. Use proper formats (JPEG for photos, PNG for graphics), compress images without sacrificing quality, and specify dimensions to improve loading times.
                </p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-primary"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">Mobile-First Approach</h3>
                <p className="text-secondary">
                  Design your website for mobile devices first, then enhance for larger screens. This ensures a good experience for the growing number of mobile users and aligns with Google's mobile-first indexing.
                </p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-primary"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">Prioritize Security</h3>
                <p className="text-secondary">
                  Implement HTTPS, keep all software updated, use strong passwords, and regularly back up your website. Security breaches can damage your reputation and result in lost business.
                </p>
              </motion.div>

              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-primary"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">Clear Call-to-Actions</h3>
                <p className="text-secondary">
                  Every page should have a clear purpose and guide users toward a specific action. Use contrasting colors, strategic positioning, and compelling text for your CTAs to improve conversion rates.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-background-alt" aria-labelledby="faq-heading">
          <div className="container-custom">
            <h2 id="faq-heading" className="text-3xl md:text-4xl font-bold text-primary mb-6 text-center">Frequently Asked Questions</h2>
            <p className="text-xl text-secondary mb-12 text-center">Common questions about web design and development</p>

            <div className="mt-12 max-w-3xl mx-auto">
              <motion.div 
                className="mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">How often should I update my website?</h3>
                <p className="text-secondary">
                  Content should be updated regularly to keep visitors engaged and improve SEO. For security and functionality, we recommend monthly maintenance to apply updates, check for issues, and ensure optimal performance. Major redesigns typically benefit businesses every 2-3 years as technology and design trends evolve.
                </p>
              </motion.div>

              <motion.div 
                className="mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">What are the most important SEO factors?</h3>
                <p className="text-secondary">
                  While SEO is complex, key factors include: quality content that addresses user needs, mobile-friendly design, fast loading speeds, secure website (HTTPS), proper site structure with clear navigation, quality backlinks, and technical elements like meta tags and schema markup. Local businesses should also focus on local SEO strategies like Google Business Profile optimization.
                </p>
              </motion.div>

              <motion.div 
                className="mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold mb-3 text-primary">How can I make my website more accessible?</h3>
                <p className="text-secondary">
                  Accessibility is essential for inclusive web design. Key practices include: providing alt text for images, maintaining sufficient color contrast, ensuring keyboard navigation, creating a logical content structure with proper heading hierarchy, adding labels to forms, making link text descriptive, and ensuring responsive design for different devices and screen readers.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CtaSection
          title="Need Expert Web Development Advice?"
          subtitle="Our team is ready to help with your specific challenges."
          buttonText="Contact Us"
          buttonLink="/contact"
        />
      </main>
    </PageTransition>
  );
};

export default AdvicePage;